import type { ProviderKind } from "./provider-profile.js";
import type { SamplingParams, TokenizerMode } from "./preset.js";

export interface TokenPricing {
  inputPerMTokUsd: number;
  outputPerMTokUsd: number;
  reasoningPerMTokUsd?: number;
  source: string;
  sourceUrl: string;
  updatedAt: string;
}

export interface ModelMetadata {
  id: string;
  aliases?: string[];
  tokenizer: TokenizerMode;
  contextSize?: number;
  pricing?: TokenPricing;
}

export interface TokenUsageForCost {
  promptTokens?: number;
  completionTokens?: number;
  reasoningTokens?: number;
}

export interface EstimatedCost {
  inputCostUsd: number;
  outputCostUsd: number;
  reasoningCostUsd: number;
  totalCostUsd: number;
  pricingSource: string;
}

type PricingSourceInfo = Pick<TokenPricing, "source" | "sourceUrl" | "updatedAt">;

const OPENAI_PRICING_SOURCE: PricingSourceInfo = {
  source: "OpenAI pricing page",
  sourceUrl: "https://platform.openai.com/docs/pricing",
  updatedAt: "2026-06-11",
};

const ANTHROPIC_PRICING_SOURCE: PricingSourceInfo = {
  source: "Anthropic model and pricing docs",
  sourceUrl: "https://docs.anthropic.com/en/docs/about-claude/models/overview",
  updatedAt: "2026-06-11",
};

const GOOGLE_PRICING_SOURCE: PricingSourceInfo = {
  source: "Google Gemini model and pricing docs",
  sourceUrl: "https://ai.google.dev/gemini-api/docs/models",
  updatedAt: "2026-06-11",
};

export const MODEL_METADATA_CATALOG: ModelMetadata[] = [
  meta("gpt-5.5", "o200k_base", 1_048_576, 5, 30, ["gpt-5.5-2026-04-23", "openai/gpt-5.5"], undefined, OPENAI_PRICING_SOURCE),
  meta("gpt-5.5-pro", "o200k_base", 272_000, 30, 180, ["openai/gpt-5.5-pro"], undefined, OPENAI_PRICING_SOURCE),
  meta("gpt-5.4", "o200k_base", 1_048_576, 2.5, 15, ["openai/gpt-5.4"], undefined, OPENAI_PRICING_SOURCE),
  meta("gpt-5.4-pro", "o200k_base", 272_000, 30, 180, ["openai/gpt-5.4-pro"], undefined, OPENAI_PRICING_SOURCE),
  meta("gpt-5.4-mini", "o200k_base", 400_000, 0.75, 4.5, ["openai/gpt-5.4-mini"], undefined, OPENAI_PRICING_SOURCE),
  meta("gpt-5.4-nano", "o200k_base", 400_000, 0.2, 1.25, ["openai/gpt-5.4-nano"], undefined, OPENAI_PRICING_SOURCE),
  meta("gpt-5.2", "o200k_base", 400_000, 1.75, 14, ["openai/gpt-5.2"], undefined, OPENAI_PRICING_SOURCE),
  meta("gpt-5.2-pro", "o200k_base", 272_000, 21, 168, ["openai/gpt-5.2-pro"], undefined, OPENAI_PRICING_SOURCE),
  meta("gpt-5.1", "o200k_base", 400_000, 1.25, 10, ["openai/gpt-5.1", "gpt-5.1-chat-latest"], undefined, OPENAI_PRICING_SOURCE),
  meta("gpt-5", "o200k_base", 400_000, 1.25, 10, ["openai/gpt-5"], undefined, OPENAI_PRICING_SOURCE),
  meta("gpt-5-mini", "o200k_base", 400_000, 0.25, 2, ["openai/gpt-5-mini"], undefined, OPENAI_PRICING_SOURCE),
  meta("gpt-5-nano", "o200k_base", 400_000, 0.05, 0.4, ["openai/gpt-5-nano"], undefined, OPENAI_PRICING_SOURCE),
  meta("gpt-4o", "o200k_base", 128_000, 2.5, 10, ["openai/gpt-4o"], undefined, OPENAI_PRICING_SOURCE),
  meta("gpt-4o-mini", "o200k_base", 128_000, 0.15, 0.6, ["openai/gpt-4o-mini"], undefined, OPENAI_PRICING_SOURCE),
  meta("gpt-4.1", "o200k_base", 1_000_000, 2, 8, ["openai/gpt-4.1"], undefined, OPENAI_PRICING_SOURCE),
  meta("gpt-4.1-mini", "o200k_base", 1_000_000, 0.4, 1.6, ["openai/gpt-4.1-mini"], undefined, OPENAI_PRICING_SOURCE),
  meta("claude-fable-5", "cl100k_base", 1_000_000, 10, 50, ["anthropic/claude-fable-5"], undefined, ANTHROPIC_PRICING_SOURCE),
  meta("claude-mythos-preview", "cl100k_base", 1_000_000, 10, 50, ["claude-mythos-5", "anthropic/claude-mythos-preview"], undefined, ANTHROPIC_PRICING_SOURCE),
  meta("claude-opus-4-8", "cl100k_base", 1_000_000, 5, 25, ["claude-opus-4.8", "anthropic/claude-opus-4.8", "anthropic/claude-opus-4-8"], undefined, ANTHROPIC_PRICING_SOURCE),
  meta("claude-sonnet-4-6", "cl100k_base", 1_000_000, 3, 15, [
    "anthropic/claude-sonnet-4.6",
    "anthropic/claude-sonnet-4-6",
  ], undefined, ANTHROPIC_PRICING_SOURCE),
  meta("claude-haiku-4-5", "cl100k_base", 200_000, 1, 5, [
    "claude-haiku-4-5-20251001",
    "anthropic/claude-haiku-4.5",
    "anthropic/claude-haiku-4-5",
  ], undefined, ANTHROPIC_PRICING_SOURCE),
  meta("gemini-3.5-flash", "cl100k_base", 1_048_576, 1.5, 9, ["google/gemini-3.5-flash"], 9, GOOGLE_PRICING_SOURCE),
  meta("gemini-3.1-pro", "cl100k_base", 1_048_576, 2, 12, ["google/gemini-3.1-pro"], 12, GOOGLE_PRICING_SOURCE),
  meta("gemini-3.1-flash-lite", "cl100k_base", 1_048_576, 0.15, 0.6, ["google/gemini-3.1-flash-lite"], 0.6, GOOGLE_PRICING_SOURCE),
  meta("gemini-3.1-pro-preview", "cl100k_base", 1_048_576, 2, 12, [
    "google/gemini-3.1-pro-preview",
    "google/gemini-3.1-pro-preview-customtools",
  ], 12, GOOGLE_PRICING_SOURCE),
  meta("gemini-2.5-pro", "cl100k_base", 1_048_576, 1.25, 10, ["google/gemini-2.5-pro"], 10, GOOGLE_PRICING_SOURCE),
  meta("gemini-2.5-flash", "cl100k_base", 1_048_576, 0.3, 2.5, ["google/gemini-2.5-flash"], 2.5, GOOGLE_PRICING_SOURCE),
  meta("gemini-2.5-flash-lite", "cl100k_base", 1_048_576, 0.1, 0.4, [
    "google/gemini-2.5-flash-lite",
  ], 0.4, GOOGLE_PRICING_SOURCE),
];

export function resolveModelMetadata(
  model: string | undefined,
  providerKind?: ProviderKind,
): ModelMetadata | undefined {
  if (!model) return undefined;
  const normalized = normalizeModelId(model);
  const providerPrefix = providerKind ? `${providerKind}/` : "";
  return MODEL_METADATA_CATALOG.find((entry) => {
    const ids = [entry.id, ...(entry.aliases ?? [])].map(normalizeModelId);
    return ids.includes(normalized) || (providerPrefix && ids.includes(`${providerPrefix}${normalized}`));
  });
}

export function resolveTokenizerMode(
  model: string | undefined,
  override?: TokenizerMode,
  providerKind?: ProviderKind,
): TokenizerMode {
  if (override && override !== "auto") return override;
  return resolveModelMetadata(model, providerKind)?.tokenizer ?? "auto";
}

export function resolvePricing(
  model: string | undefined,
  sampling?: SamplingParams,
  providerKind?: ProviderKind,
): TokenPricing | undefined {
  const meta = resolveModelMetadata(model, providerKind);
  const base = meta?.pricing;
  const hasOverride =
    sampling?.priceInputPerMTok != null ||
    sampling?.priceOutputPerMTok != null ||
    sampling?.priceReasoningPerMTok != null;
  if (!hasOverride) return base;
  return {
    inputPerMTokUsd: sampling?.priceInputPerMTok ?? base?.inputPerMTokUsd ?? 0,
    outputPerMTokUsd: sampling?.priceOutputPerMTok ?? base?.outputPerMTokUsd ?? 0,
    ...(sampling?.priceReasoningPerMTok != null || base?.reasoningPerMTokUsd != null
      ? { reasoningPerMTokUsd: sampling?.priceReasoningPerMTok ?? base?.reasoningPerMTokUsd }
      : {}),
    source: "Preset override",
    sourceUrl: base?.sourceUrl ?? "",
    updatedAt: new Date().toISOString().slice(0, 10),
  };
}

export function estimateTokenCost(
  usage: TokenUsageForCost,
  pricing: TokenPricing | undefined,
): EstimatedCost | undefined {
  if (!pricing) return undefined;
  const inputCostUsd = ((usage.promptTokens ?? 0) / 1_000_000) * pricing.inputPerMTokUsd;
  const outputCostUsd = ((usage.completionTokens ?? 0) / 1_000_000) * pricing.outputPerMTokUsd;
  const reasoningCostUsd =
    ((usage.reasoningTokens ?? 0) / 1_000_000) *
    (pricing.reasoningPerMTokUsd ?? pricing.outputPerMTokUsd);
  return {
    inputCostUsd,
    outputCostUsd,
    reasoningCostUsd,
    totalCostUsd: inputCostUsd + outputCostUsd + reasoningCostUsd,
    pricingSource: pricing.source,
  };
}

function meta(
  id: string,
  tokenizer: TokenizerMode,
  contextSize: number,
  inputPerMTokUsd: number,
  outputPerMTokUsd: number,
  aliases: string[] = [],
  reasoningPerMTokUsd?: number,
  source: PricingSourceInfo = OPENAI_PRICING_SOURCE,
): ModelMetadata {
  return {
    id,
    aliases,
    tokenizer,
    contextSize,
    pricing: {
      inputPerMTokUsd,
      outputPerMTokUsd,
      ...(reasoningPerMTokUsd != null ? { reasoningPerMTokUsd } : {}),
      ...source,
    },
  };
}

function normalizeModelId(id: string): string {
  return id.toLowerCase().replace(/^models\//, "");
}
